'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { Fragment } from 'react';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { findDOMNode } from 'react-dom';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIFlex from 'UIComponents/layout/UIFlex';
import CompanyLogo, { COMPANY_LOGO_USE } from 'MeetingsLib/components/decorative/CompanyLogo';
import CompactBookingPage from 'MeetingsLib/components/page/CompactBookingPage';
import FullSizeBookingPage from 'MeetingsLib/components/page/FullSizeBookingPage';
import RescheduleHeader from 'MeetingsLib/components/page/RescheduleHeader';
import TroubleshootModeToggle from 'MeetingsLib/components/header/TroubleshootModeToggle';
import BookingStepIndicator from 'MeetingsLib/components/sections/BookingStepIndicator';
import { canSubmitInitialPropertyForm as canSubmitInitialPropertyFormSelector } from 'MeetingsLib/selectors/AutoSubmitSelector';
import { shouldShowEmailForm as shouldShowEmailFormSelector, getLegalConsentEnabled, getRecaptchaEnabled, getPaidMeetingsAvailableSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getLegalConsentResponses, getAllRequiredConsentCollected } from 'MeetingsLib/selectors/LegalConsentFormSelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { canTroubleshootSelector } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import * as BookActions from 'MeetingsLib/redux/actions/BookActions';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import * as UIActions from 'MeetingsLib/redux/actions/UIActions';
import * as TroubleshootActions from 'MeetingsLib/redux/actions/TroubleshootActions';
import UserProfile from 'MeetingsBase/models/UserProfile';
import Branding from 'MeetingsBase/models/Branding';
import UserMessage from 'MeetingsLib/models/UserMessage';
import BookingLifecycle from 'MeetingsLib/models/BookingLifecycle';
import FormState from 'MeetingsLib/models/FormState';
import WelcomeScreenInfo from 'MeetingsBase/models/WelcomeScreenInfo';
import { BOOKING_STEPS } from 'MeetingsLib/constants/steps';

class Booking extends React.Component {
  constructor(...args) {
    super(...args);

    this.getContainerNode = element => {
      if (element) {
        this.props.setContainerNodeRef(findDOMNode(element));
      }
    };

    this.getCurrentStep = () => {
      const {
        bookingLifecycle,
        formState,
        isPaidMeeting,
        legalConsentValid
      } = this.props;

      if (!bookingLifecycle.event.dateString || !bookingLifecycle.event.dateTime) {
        return BOOKING_STEPS.CHOOSE_TIME;
      }

      if (!formState || !legalConsentValid) {
        return BOOKING_STEPS.YOUR_INFO;
      }

      if (isPaidMeeting) {
        return BOOKING_STEPS.PAYMENT;
      }

      return null;
    };

    this.getBookingComponent = () => {
      return this.props.compact ? CompactBookingPage : FullSizeBookingPage;
    };

    this.renderBookInterface = () => {
      const {
        avatar,
        companyAvatar,
        bookingLifecycle,
        displayAvatarOption,
        emailFormState,
        formState,
        recaptchaEnabled,
        legalConsentEnabled,
        legalConsentResponses,
        legalConsentValid,
        branding,
        canSubmitInitialPropertyForm,
        shouldShowEmailForm,
        canTroubleshoot,
        embedded,
        headline,
        isFetchingPublicAvailability,
        isPaidMeeting,
        profile,
        welcomeScreenInfo,
        submittingMeeting,
        DatePickerMarker,
        TimePickerMarker,
        EmailFormMarker
      } = this.props;
      const Component = this.getBookingComponent();
      return /*#__PURE__*/_jsx(Component, {
        avatar: avatar,
        companyAvatar: companyAvatar,
        displayAvatarOption: displayAvatarOption,
        bookingLifecycle: bookingLifecycle,
        emailFormState: emailFormState,
        formState: formState,
        recaptchaEnabled: recaptchaEnabled,
        legalConsentEnabled: legalConsentEnabled,
        legalConsentResponses: legalConsentResponses,
        legalConsentValid: legalConsentValid,
        branding: branding,
        canSubmitInitialPropertyForm: canSubmitInitialPropertyForm,
        canTroubleshoot: canTroubleshoot,
        embedded: embedded,
        headline: headline,
        isFetchingPublicAvailability: isFetchingPublicAvailability,
        profile: profile,
        welcomeScreenInfo: welcomeScreenInfo,
        shouldShowEmailForm: shouldShowEmailForm,
        submittingMeeting: submittingMeeting,
        DatePickerMarker: DatePickerMarker,
        TimePickerMarker: TimePickerMarker,
        EmailFormMarker: EmailFormMarker,
        currentStep: this.getCurrentStep(),
        isPaidMeeting: isPaidMeeting
      });
    };

    this.renderPotentialAlert = () => {
      const {
        userMessage
      } = this.props;

      if (userMessage) {
        return /*#__PURE__*/_jsx("div", {
          className: "m-all-6",
          children: /*#__PURE__*/_jsx(UIAlert, {
            "data-selenium": "booking-alert-message",
            type: userMessage.type,
            children: userMessage.content
          })
        });
      }

      return null;
    };

    this.renderCompanyLogo = () => {
      const {
        branding,
        compact,
        embedded,
        hideLogo
      } = this.props;

      if (embedded || hideLogo) {
        return null;
      }

      return /*#__PURE__*/_jsx(CompanyLogo, {
        use: COMPANY_LOGO_USE.BOOKING_CONTAINER,
        compact: compact,
        branding: branding
      });
    };

    this.renderRescheduleHeader = () => {
      const {
        isRescheduling
      } = this.props;
      return isRescheduling && /*#__PURE__*/_jsx(RescheduleHeader, {});
    };

    this.renderTroubleshootModeToggle = () => {
      const {
        canTroubleshoot,
        compact,
        troubleshooting,
        toggleTroubleshootMode,
        fetchTroubleshootTimes,
        isRescheduling
      } = this.props;

      if (isRescheduling || !canTroubleshoot) {
        return null;
      }

      return !compact && /*#__PURE__*/_jsx(TroubleshootModeToggle, {
        canTroubleshoot: canTroubleshoot,
        troubleshooting: troubleshooting,
        toggleTroubleshootMode: toggleTroubleshootMode,
        fetchTroubleshootTimes: fetchTroubleshootTimes
      });
    };
  }

  render() {
    const {
      compact,
      embedded,
      shouldShowEmailForm,
      emailFormState,
      isPaidMeeting,
      isPreviewed
    } = this.props;
    const classes = 'meetings-page' + (isPreviewed && compact ? " p-bottom-12" : "");
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [!(shouldShowEmailForm && !emailFormState) && /*#__PURE__*/_jsx(BookingStepIndicator, {
        compact: compact,
        currStep: this.getCurrentStep(),
        embedded: embedded,
        isPreviewed: isPreviewed,
        withPayment: isPaidMeeting
      }), /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        align: "center",
        className: classes,
        ref: this.getContainerNode,
        children: [this.renderCompanyLogo(), this.renderRescheduleHeader(), this.renderTroubleshootModeToggle(), this.renderPotentialAlert(), this.renderBookInterface()]
      })]
    });
  }

}

Booking.propTypes = {
  // passed
  avatar: PropTypes.string,
  companyAvatar: PropTypes.string,
  branding: PropTypes.instanceOf(Branding).isRequired,
  displayAvatarOption: PropTypes.string,
  welcomeScreenInfo: PropTypes.instanceOf(WelcomeScreenInfo).isRequired,
  headline: PropTypes.string,
  hideLogo: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile).isRequired,
  submittingMeeting: PropTypes.bool,
  DatePickerMarker: PropTypes.func.isRequired,
  TimePickerMarker: PropTypes.func.isRequired,
  EmailFormMarker: PropTypes.func.isRequired,
  compact: PropTypes.bool.isRequired,
  setContainerNodeRef: PropTypes.func.isRequired,
  isPreviewed: PropTypes.bool,
  // loaded
  bookingLifecycle: PropTypes.instanceOf(BookingLifecycle).isRequired,
  emailFormState: PropTypes.string,
  formState: PropTypes.instanceOf(FormState),
  recaptchaEnabled: PropTypes.bool.isRequired,
  legalConsentEnabled: PropTypes.bool,
  legalConsentResponses: PropTypes.instanceOf(List),
  legalConsentValid: PropTypes.bool,
  canSubmitInitialPropertyForm: PropTypes.bool,
  embedded: PropTypes.bool.isRequired,
  isFetchingPublicAvailability: PropTypes.bool.isRequired,
  isRescheduling: PropTypes.bool,
  canTroubleshoot: PropTypes.bool,
  troubleshooting: PropTypes.bool.isRequired,
  toggleTroubleshootMode: PropTypes.func.isRequired,
  fetchTroubleshootTimes: PropTypes.func.isRequired,
  shouldShowEmailForm: PropTypes.bool,
  userMessage: PropTypes.instanceOf(UserMessage),
  isPaidMeeting: PropTypes.bool
};
export default connect(state => ({
  canSubmitInitialPropertyForm: canSubmitInitialPropertyFormSelector(state),
  canTroubleshoot: canTroubleshootSelector(state),
  embedded: state.embedded,
  eventDetails: state.eventDetails,
  isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
  isSalesStarter: state.isSalesStarter,
  shouldShowEmailForm: shouldShowEmailFormSelector(state),
  emailFormState: state.emailFormState,
  troubleshooting: state.troubleshooting.active,
  userMessage: state.userMessage,
  bookingLifecycle: state.bookingLifecycle,
  formState: state.formState,
  legalConsentEnabled: getLegalConsentEnabled(state),
  recaptchaEnabled: getRecaptchaEnabled(state),
  legalConsentResponses: getLegalConsentResponses(state),
  legalConsentValid: getAllRequiredConsentCollected(state),
  isRescheduling: state.isRescheduling,
  isPaidMeeting: getPaidMeetingsAvailableSelector(state)
}), Object.assign({}, BookActions, {}, FormActions, {}, UIActions, {}, TroubleshootActions))(Booking);